import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Share2, MessageCircle, Send, Trash2, Download, Copy, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

/**
 * Match Social — Shareable result card + emoji reactions + comments
 * Props:
 *   match: { id, player1_name, player2_name, sets, winner_id, player1_id, player2_id, completed, round, group_name }
 *   tournamentName?: string
 *   tournamentDate?: string
 *   compact?: boolean   - render small inline buttons (default true)
 */
const REACTIONS_FALLBACK = [
  { type: 'like', emoji: '👍' },
  { type: 'fire', emoji: '🔥' },
  { type: 'tennis', emoji: '🎾' },
  { type: 'muscle', emoji: '💪' },
  { type: 'goat', emoji: '🐐' },
];

const MatchSocial = ({ match, tournamentName = '', tournamentDate = '', compact = true }) => {
  const { API, user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('reactions'); // reactions | comments | share
  const [reactions, setReactions] = useState(REACTIONS_FALLBACK.map(r => ({ ...r, count: 0, reacted: false })));
  const [totalReactions, setTotalReactions] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const cardRef = useRef(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadReactions = async () => {
    try {
      const res = await axios.get(`${API}/matches/${match.id}/reactions`, { headers: authHeaders() });
      setReactions(res.data.counts || REACTIONS_FALLBACK);
      setTotalReactions(res.data.total || 0);
    } catch {}
  };

  const loadComments = async () => {
    try {
      const res = await axios.get(`${API}/comments/match/${match.id}`);
      setComments(res.data.comments || []);
    } catch {}
  };

  useEffect(() => {
    if (match?.id) loadReactions();
    // eslint-disable-next-line
  }, [match?.id]);

  useEffect(() => {
    if (open && tab === 'comments') loadComments();
    // eslint-disable-next-line
  }, [open, tab]);

  const toggleReaction = async (type) => {
    if (!user) { toast.error('עליך להתחבר כדי להגיב'); return; }
    // optimistic
    setReactions(prev => prev.map(r => r.type === type ? { ...r, reacted: !r.reacted, count: r.count + (r.reacted ? -1 : 1) } : r));
    setTotalReactions(t => t + (reactions.find(r => r.type === type)?.reacted ? -1 : 1));
    try {
      await axios.post(`${API}/matches/${match.id}/reactions`, { reaction_type: type }, { headers: authHeaders() });
    } catch (e) {
      toast.error('שגיאה');
      loadReactions();
    }
  };

  const postComment = async () => {
    if (!user) { toast.error('עליך להתחבר'); return; }
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/comments`, { match_id: match.id, content: newComment.trim() }, { headers: authHeaders() });
      setNewComment('');
      loadComments();
    } catch (e) {
      toast.error('שגיאה בפרסום תגובה');
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm('למחוק את התגובה?')) return;
    try {
      await axios.delete(`${API}/comments/${cid}`, { headers: authHeaders() });
      loadComments();
    } catch (e) { toast.error('שגיאה'); }
  };

  // === Share helpers ===
  const winnerName = match.winner_id === match.player1_id ? match.player1_name
    : match.winner_id === match.player2_id ? match.player2_name : null;
  const setsText = (match.sets || []).map(s => `${s.player1_games ?? s.player1_score ?? 0}-${s.player2_games ?? s.player2_score ?? 0}`).join(', ');
  const shareText = winnerName
    ? `🎾 ${winnerName} ניצח ב${tournamentName || 'תחרות'}!\nתוצאה: ${match.player1_name} מול ${match.player2_name} (${setsText})\n\nצפה ב-tennisproisrael.co.il 🏆`
    : `🎾 ${match.player1_name} מול ${match.player2_name} — ${setsText}\n${tournamentName}`;

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 2 });
      const link = document.createElement('a');
      link.download = `tennis-result-${match.id}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('התמונה הורדה');
    } catch (e) { toast.error('שגיאה בהורדה'); }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'Tennis Pro Israel', text: shareText, url: window.location.href }); } catch {}
    } else {
      navigator.clipboard.writeText(shareText + '\n' + window.location.href);
      toast.success('הטקסט הועתק! הדבק ב-WhatsApp');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('הקישור הועתק');
  };

  if (!match || !match.completed) return null;

  return (
    <>
      {/* Compact buttons row */}
      <div className="flex items-center gap-1.5 flex-wrap" dir="rtl">
        {/* Reaction summary */}
        <button
          onClick={(e) => { e.stopPropagation(); setTab('reactions'); setOpen(true); }}
          data-testid={`match-reactions-${match.id}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-xs transition"
        >
          {reactions.slice(0, 3).filter(r => r.count > 0).map(r => <span key={r.type}>{r.emoji}</span>)}
          {totalReactions === 0 && <span>👍</span>}
          <span className="font-bold text-slate-700">{totalReactions || 'הגב'}</span>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setTab('comments'); setOpen(true); loadComments(); }}
          data-testid={`match-comments-${match.id}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 hover:bg-blue-100 text-xs text-blue-700 transition"
        >
          <MessageCircle className="w-3 h-3" />
          {comments.length > 0 ? <span className="font-bold">{comments.length}</span> : <span>דון</span>}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setTab('share'); setOpen(true); }}
          data-testid={`match-share-${match.id}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 hover:bg-emerald-100 text-xs text-emerald-700 transition"
        >
          <Share2 className="w-3 h-3" />
          <span>שתף</span>
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-2xl" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-right">
              🎾 {match.player1_name} מול {match.player2_name}
            </DialogTitle>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-1 border-b mb-3">
            {[
              { key: 'reactions', label: `הגב (${totalReactions})`, icon: '👍' },
              { key: 'comments', label: `תגובות (${comments.length})`, icon: '💬' },
              { key: 'share', label: 'שתף', icon: '📤' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); if (t.key === 'comments') loadComments(); }}
                data-testid={`match-social-tab-${t.key}`}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === t.key ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* REACTIONS */}
          {tab === 'reactions' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">בחר אימוג'י כדי להגיב למשחק</p>
              <div className="grid grid-cols-5 gap-2">
                {reactions.map(r => (
                  <button
                    key={r.type}
                    onClick={() => toggleReaction(r.type)}
                    data-testid={`reaction-${r.type}`}
                    className={`flex flex-col items-center gap-1 py-3 rounded-xl border-2 transition ${r.reacted ? 'bg-emerald-50 border-emerald-400 scale-105' : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
                  >
                    <span className="text-3xl">{r.emoji}</span>
                    <span className="text-xs font-bold text-slate-700">{r.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* COMMENTS */}
          {tab === 'comments' && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {user ? (
                <div className="flex gap-2">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="כתוב תגובה..."
                    rows={2}
                    className="flex-1 resize-none"
                    data-testid="match-comment-input"
                  />
                  <Button onClick={postComment} disabled={posting || !newComment.trim()} className="bg-emerald-600 hover:bg-emerald-700 self-end" data-testid="match-comment-submit">
                    <Send className="w-4 h-4 ml-1" />שלח
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center p-3 bg-slate-50 rounded">התחבר כדי להגיב</p>
              )}

              {comments.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">אין תגובות עדיין. היה הראשון להגיב 💬</p>
              ) : (
                <div className="space-y-2">
                  {comments.map(c => (
                    <div key={c.id} className="flex gap-2 p-3 bg-slate-50 rounded-lg">
                      <Avatar className="w-8 h-8 flex-shrink-0">
                        {c.user_avatar && <AvatarImage src={c.user_avatar} />}
                        <AvatarFallback className="bg-slate-600 text-white text-xs">{(c.user_name || '?').charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-sm text-slate-800">{c.user_name}</span>
                          <span className="text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        </div>
                        <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{c.content || c.text}</p>
                      </div>
                      {user && (user.id === c.user_id || user.is_admin) && (
                        <button onClick={() => deleteComment(c.id)} className="text-red-500 hover:text-red-700 self-start">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SHARE */}
          {tab === 'share' && (
            <div className="space-y-4">
              {/* Beautiful shareable card */}
              <div
                ref={cardRef}
                className="relative overflow-hidden rounded-2xl p-6 text-white shadow-2xl"
                style={{
                  background: 'linear-gradient(135deg, #064e3b 0%, #047857 30%, #0e7490 70%, #1e3a8a 100%)',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
              >
                <div className="absolute -top-12 -right-12 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
                <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-yellow-400/10 rounded-full blur-2xl" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-xs uppercase tracking-widest opacity-80 font-bold">Tennis Pro Israel</div>
                    <div className="text-xs opacity-70">{tournamentDate ? new Date(tournamentDate).toLocaleDateString('he-IL') : ''}</div>
                  </div>
                  {tournamentName && (
                    <div className="text-sm font-semibold opacity-90 mb-3">{tournamentName}{match.round ? ` · ${match.round}` : ''}{match.group_name ? ` · ${match.group_name}` : ''}</div>
                  )}
                  <div className="grid grid-cols-[1fr,auto,1fr] gap-3 items-center my-6">
                    {/* Player 1 */}
                    <div className={`text-right ${match.winner_id === match.player1_id ? 'opacity-100' : 'opacity-60'}`}>
                      <div className="text-2xl font-black truncate">{match.player1_name}</div>
                      {match.winner_id === match.player1_id && (
                        <div className="inline-flex items-center gap-1 mt-1 text-xs font-bold bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full">
                          <Trophy className="w-3 h-3" /> ניצח
                        </div>
                      )}
                    </div>
                    <div className="text-3xl font-black opacity-50">VS</div>
                    {/* Player 2 */}
                    <div className={`text-left ${match.winner_id === match.player2_id ? 'opacity-100' : 'opacity-60'}`}>
                      <div className="text-2xl font-black truncate">{match.player2_name}</div>
                      {match.winner_id === match.player2_id && (
                        <div className="inline-flex items-center gap-1 mt-1 text-xs font-bold bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full">
                          <Trophy className="w-3 h-3" /> ניצח
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Sets */}
                  <div className="flex items-center justify-center gap-2 mt-4">
                    {(match.sets || []).map((s, i) => {
                      const p1 = s.player1_games ?? s.player1_score ?? 0;
                      const p2 = s.player2_games ?? s.player2_score ?? 0;
                      return (
                        <div key={i} className="bg-white/15 backdrop-blur rounded-lg px-3 py-2 text-center min-w-[55px]">
                          <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">סט {i + 1}</div>
                          <div className="text-lg font-black tabular-nums">{p1}-{p2}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-center text-xs opacity-70 mt-5 tracking-wider">tennisproisrael.co.il 🎾</div>
                </div>
              </div>

              {/* Share actions */}
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={shareNative} className="bg-emerald-600 hover:bg-emerald-700" data-testid="share-native-btn">
                  <Share2 className="w-4 h-4 ml-1" />שתף ב-WhatsApp
                </Button>
                <Button onClick={downloadCard} variant="outline" data-testid="share-download-btn">
                  <Download className="w-4 h-4 ml-1" />הורד תמונה
                </Button>
                <Button onClick={() => { navigator.clipboard.writeText(shareText); toast.success('הטקסט הועתק'); }} variant="outline">
                  <Copy className="w-4 h-4 ml-1" />העתק טקסט
                </Button>
                <Button onClick={copyLink} variant="outline">
                  <Copy className="w-4 h-4 ml-1" />העתק קישור
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MatchSocial;
